import classNames from 'classnames'
import React, { FC, useEffect, useMemo, useState, type JSX } from 'react'
import { useTranslation } from 'react-i18next'

import { Body1, Caption1 } from '@ui/Typography'
import { Button } from '@ui/components/Button'
import { Checkbox } from '@ui/components/Checkbox'
import { Input } from '@ui/components/Input'
import { Link } from '@ui/components/Link'
import { Radio } from '@ui/components/Radio'

import { useGAEvents } from '@utils/callGAEvents'
import { getDocFromTest } from '@utils/ibTests'
import { isOne } from '@utils/validations'

import { ClientTestDto } from '@model/ClientTestDto'
import { OldLegalDocumentDto } from '@model/LegalDocumentDto'
import {
  FreeTextAnswer,
  QuestionDto,
  TestQuestionWidgetType,
  TestSectionAnswerDto,
  TestSectionQuestionDto,
  WidgetOptions,
} from '@model/TestSectionsDto'

import { SelectField } from '@global/field/SelectField'

import { Box } from '@shared/components/Box'

import styles from './IBQuestionnaire.module.scss'

interface IBQuestionnaireProps {
  onSubmit: (selectedAnswers: string[], freeAnswers: FreeTextAnswer[]) => void
  testData: ClientTestDto
  legalDocuments: OldLegalDocumentDto[]
  isMultiTier: boolean
}

export const IBQuestionnaire: FC<IBQuestionnaireProps> = (props) => {
  const { onSubmit, testData, legalDocuments, isMultiTier } = props

  const [selectedAnswers, setSelectedAnswers] = useState<string[]>([])
  const [freeAnswers, setFreeAnswers] = useState<FreeTextAnswer[]>([])
  const [showErrors, setShowErrors] = useState(false)
  const allQuestions = useMemo(
    () => testData.sections.flatMap((section) => section.questions),
    [testData.sections]
  )

  const { t } = useTranslation()
  const errors = useValidate(testData, allQuestions, selectedAnswers, freeAnswers)
  const { callGAEvents } = useGAEvents()

  const buildTestQuestion = (question: QuestionDto, isRequired?: boolean) => {
    const widgetName = question.widgetName || ''
    const docUrl = legalDocuments.find(({ code }) => code === getDocFromTest(widgetName))?.url
    if (!docUrl) {
      return <span className={classNames({ [styles.required]: isRequired })}>{question.name}</span>
    }

    return (
      <Link size={'lg'} href={docUrl}>
        <span className={classNames({ [styles.required]: isRequired })}>{question.name}</span>
      </Link>
    )
  }

  const fireGAEvent = (question: string, answer: string | boolean | number) => {
    const eventProps = {
      ca_question: question,
      ca_selected_answer: answer.toString(),
    }

    if (isMultiTier) {
      callGAEvents(eventProps).IBQuestionnaire.Questions.multiTierSelect()
    } else {
      callGAEvents(eventProps).IBQuestionnaire.Questions.standardSelect()
    }
  }

  const handlePickOption = (
    answerId: string,
    questionAnswers: TestSectionAnswerDto[],
    questionName: string
  ) => {
    if (selectedAnswers.includes(answerId)) {
      return selectedAnswers
    }

    const answerdIds = questionAnswers.map((answer) => answer.id)
    const cleanedValues = selectedAnswers.filter((value) => !answerdIds.includes(value)) ?? []
    cleanedValues.push(answerId)
    fireGAEvent(
      questionName,
      questionAnswers
        .filter((answer) => cleanedValues.includes(answer.id))
        .map((answer) => answer.name)
        .toString()
    )
    return cleanedValues
  }

  const handleMultiSelect = (
    answers: TestSectionAnswerDto[],
    answerId: string,
    questionName: string
  ) => {
    let allAnswers
    if (selectedAnswers.includes(answerId)) {
      allAnswers = selectedAnswers.filter((value) => value !== answerId)
    } else {
      allAnswers = [...selectedAnswers, answerId]
    }

    const relevantAnswers = answers.filter((answer) => allAnswers.includes(answer.id))
    const answerNames = relevantAnswers.map(
      (rAnswer) => answers.find((answer) => answer.id === rAnswer.id)?.name
    )

    fireGAEvent(questionName, answerNames.join(', '))
    return allAnswers
  }

  const handleCheckbox = (testQuestionId: string, questionName: string) => {
    const existingAnswer = freeAnswers.find(({ testQuestionId: id }) => id === testQuestionId)
    fireGAEvent(questionName, !isOne(existingAnswer?.answer))

    if (existingAnswer) {
      if (isOne(existingAnswer.answer)) {
        const updatedAnswers = freeAnswers.filter(({ testQuestionId: id }) => id !== testQuestionId)
        setFreeAnswers([...updatedAnswers, { testQuestionId, answer: 0 }])
      } else {
        const updatedAnswers = freeAnswers.filter(({ testQuestionId: id }) => id !== testQuestionId)
        setFreeAnswers([...updatedAnswers, { testQuestionId, answer: 1 }])
      }
    } else {
      setFreeAnswers([...freeAnswers, { testQuestionId, answer: 1 }])
    }
  }

  const handleFreeText = (testQuestionId: string, answer: string | null | number) => {
    const existingAnswer = freeAnswers.find(({ testQuestionId: id }) => id === testQuestionId)
    if (existingAnswer) {
      const updatedAnswers = freeAnswers.filter(({ testQuestionId: id }) => id !== testQuestionId)
      setFreeAnswers([...updatedAnswers, { testQuestionId, answer }])
    } else {
      setFreeAnswers([...freeAnswers, { testQuestionId, answer }])
    }
  }

  const agreementBoxes: JSX.Element[] = []

  const handleSubmitClick = () => {
    callGAEvents().IBQuestionnaire.Actions.becomeIB()
    onSubmit(selectedAnswers, freeAnswers)
  }

  return (
    <div className={styles.ibQuestionnaire}>
      {testData.sections.map(({ questions }) =>
        questions
          .filter(({ widget }) =>
            showConditionalQuestions(allQuestions, selectedAnswers, widget.widgetOptions)
          )
          .map(({ question, answers, isMandatory, widget }) => {
            const isRequired = isMandatory || widget.widgetOptions?.isMandatory

            switch (question.widgetType) {
              case TestQuestionWidgetType.Radio:
                return (
                  <QuestionBox
                    key={question.id}
                    showErrors={showErrors}
                    error={errors[question.id]}
                    label={question.name}
                    required={isRequired}
                  >
                    {answers.map((answer) => (
                      <div className={styles.selectorBox} key={answer.id}>
                        <Radio
                          name={'questionnaire'}
                          onChange={() =>
                            setSelectedAnswers(handlePickOption(answer.id, answers, question.name))
                          }
                          checked={selectedAnswers.includes(answer.id)}
                        >
                          {answer.name}
                        </Radio>
                      </div>
                    ))}
                  </QuestionBox>
                )

              case TestQuestionWidgetType.Text:
              case TestQuestionWidgetType.ShortText:
                return (
                  <QuestionBox
                    key={question.id}
                    showErrors={showErrors}
                    error={errors[question.id]}
                    label={question.name}
                    required={isRequired}
                  >
                    <Input
                      maxLength={255}
                      fullWidth
                      type='text'
                      value={
                        freeAnswers.find(({ testQuestionId }) => testQuestionId === question.id)
                          ?.answer ?? ''
                      }
                      onChange={(e) => handleFreeText(question.id, e.target.value)}
                    />
                  </QuestionBox>
                )

              case TestQuestionWidgetType.Select:
                return (
                  <QuestionBox
                    key={question.id}
                    showErrors={showErrors}
                    error={errors[question.id]}
                    label={question.name}
                    required={isRequired}
                  >
                    <SelectField
                      options={answers.map((answer) => ({
                        label: answer.name,
                        value: answer.id,
                      }))}
                      fullWidth
                      onChange={(v) =>
                        setSelectedAnswers(handlePickOption(v.target.value, answers, question.name))
                      }
                      value={answers.find((answer) => selectedAnswers.includes(answer.id))?.id}
                      disabled={false}
                    />
                  </QuestionBox>
                )

              case TestQuestionWidgetType.Multiselect:
                return (
                  <QuestionBox
                    key={question.id}
                    showErrors={showErrors}
                    error={errors[question.id]}
                    label={question.name}
                    required={isRequired}
                  >
                    {answers.map((answer) => (
                      <div className={styles.selectorBox} key={answer.id}>
                        <Checkbox
                          onChange={() =>
                            setSelectedAnswers(handleMultiSelect(answers, answer.id, question.name))
                          }
                          checked={selectedAnswers.includes(answer.id)}
                        >
                          <span>{answer.name}</span>
                        </Checkbox>
                      </div>
                    ))}
                  </QuestionBox>
                )

              case TestQuestionWidgetType.Checkbox:
                agreementBoxes.push(
                  <div key={question.id}>
                    <Checkbox
                      onChange={() => handleCheckbox(question.id, question.name)}
                      checked={isOne(
                        freeAnswers.find(({ testQuestionId }) => testQuestionId === question.id)
                          ?.answer
                      )}
                    >
                      {buildTestQuestion(question, isRequired)}
                    </Checkbox>
                    {showErrors && errors[question.id] && (
                      <Caption1 className={styles.error}>{errors[question.id]}</Caption1>
                    )}
                  </div>
                )
                return null

              default:
                return null
            }
          })
      )}
      {agreementBoxes.length > 0 && (
        <div className={styles.agreementBoxWrapper}>{agreementBoxes}</div>
      )}
      <div className={styles.submit}>
        <Box mt='x32' className={styles.submitButton}>
          <Button.Primary
            size='lg'
            disabled={Object.values(errors).some((error) => error.length > 0)}
            onClick={() =>
              Object.values(errors).some((error) => error.length > 0)
                ? setShowErrors(true)
                : handleSubmitClick()
            }
          >
            {isMultiTier ? t('Submit') : t('IB.Become an Introducing Broker')}
          </Button.Primary>
        </Box>
      </div>
    </div>
  )
}

const showConditionalQuestions = (
  questions: readonly TestSectionQuestionDto[],
  selectedAnswers: string[],
  rule?: WidgetOptions | null
) => {
  const dependsOn = questions.find((question) => question.widget.name === rule?.depend)
  if (!dependsOn) {
    return true
  }

  const dependsOnValueWidgetData = dependsOn.widget.widgetOptions
  const answersNeededToShow = dependsOn.answers.find((answer) =>
    dependsOnValueWidgetData?.onChange?.some((onchange) => onchange.values?.includes(answer.id))
  )?.id

  return selectedAnswers.some((answer) => answersNeededToShow?.includes(answer))
}

interface QuestionBoxProps {
  children: React.ReactNode
  label?: string
  error?: string
  showErrors?: boolean
  required?: boolean
}

const QuestionBox: React.FC<QuestionBoxProps> = ({
  children,
  label,
  error,
  showErrors,
  required,
}) => {
  return (
    <div className={styles.questionBox}>
      {label && (
        <Body1 className={classNames({ [styles.required]: required })} medium>
          {label}
        </Body1>
      )}
      <div className={styles.fieldGroup}>{children}</div>
      {error && showErrors && <Caption1 className={styles.error}>{error}</Caption1>}
    </div>
  )
}

const useValidate = (
  testData: ClientTestDto,
  allQuestions: TestSectionQuestionDto[],
  selectedAnswers: string[],
  freeAnswers: FreeTextAnswer[]
) => {
  const { t } = useTranslation()
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    const newErrors: Record<string, string> = {}

    testData.sections.map((section) =>
      section.questions
        .filter(({ isMandatory, widget }) => {
          return (
            isMandatory ||
            (showConditionalQuestions(allQuestions, selectedAnswers, widget.widgetOptions) &&
              widget.widgetOptions?.isMandatory)
          )
        })
        .map(({ question, answers }) => {
          switch (question.widgetType) {
            case TestQuestionWidgetType.Radio:
            case TestQuestionWidgetType.Select:
              if (!selectedAnswers.some((answer) => answers.map(({ id }) => id).includes(answer))) {
                newErrors[question.id] = t('Validation.Please select an option')
              }
              break

            case TestQuestionWidgetType.Multiselect:
              if (!selectedAnswers.some((answer) => answers.map(({ id }) => id).includes(answer))) {
                newErrors[question.id] = t('Validation.Please select at least one option')
              }
              break

            case TestQuestionWidgetType.Checkbox:
              if (
                !isOne(
                  freeAnswers.find(({ testQuestionId }) => testQuestionId === question.id)?.answer
                )
              ) {
                newErrors[question.id] = t('Validation.This selection is required')
              }
              break

            case TestQuestionWidgetType.Text:
            case TestQuestionWidgetType.ShortText:
              if (
                !freeAnswers.find(({ testQuestionId }) => testQuestionId === question.id)?.answer
              ) {
                newErrors[question.id] = t('Validation.Please fill the value')
              }
              break

            default:
              break
          }
        })
    )

    setErrors(newErrors)
  }, [allQuestions, testData, selectedAnswers, freeAnswers, t])

  return errors
}
